/**
 * Navigation configuration for cross-app navigation
 * Handles redirects from marketing website to the food ordering app
 */

export interface NavigationConfig {
  appWebUrl: string;
  defaultRoute: string;
}

/**
 * Default configuration values
 */
const DEFAULT_APP_WEB_URL = 'http://localhost:3002';
const DEFAULT_ROUTE = '/restaurants';

/**
 * Validates if a URL string is properly formatted
 */
function isValidUrl(urlString: string): boolean {
  try {
    const url = new URL(urlString);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Gets the app-web URL from environment variables with fallback
 * Logs warnings in development when using fallback values
 */
function getAppWebUrl(): string {
  const envUrl = import.meta.env.VITE_APP_WEB_URL;
  
  // Check if environment variable is missing
  if (!envUrl || envUrl.trim() === '') {
    if (import.meta.env.DEV) {
      console.warn(
        `[Navigation] VITE_APP_WEB_URL is not configured. Falling back to default: ${DEFAULT_APP_WEB_URL}`
      );
    }
    return DEFAULT_APP_WEB_URL;
  }
  
  // Validate URL format
  if (!isValidUrl(envUrl)) {
    if (import.meta.env.DEV) {
      console.error(
        `[Navigation] VITE_APP_WEB_URL is invalid: "${envUrl}". Falling back to default: ${DEFAULT_APP_WEB_URL}`
      );
    }
    return DEFAULT_APP_WEB_URL;
  }
  
  return envUrl;
}

/**
 * Gets the navigation configuration
 */
export function getNavigationConfig(): NavigationConfig {
  return {
    appWebUrl: getAppWebUrl(),
    defaultRoute: DEFAULT_ROUTE,
  };
}

/**
 * Constructs a full URL to the app-web application
 * @param route - Optional route path (defaults to /restaurants)
 * @param queryParams - Optional query parameters to append
 */
export function buildAppWebUrl(
  route?: string,
  queryParams?: Record<string, string>
): string {
  const config = getNavigationConfig();
  const targetRoute = route || config.defaultRoute;
  
  // Ensure route starts with /
  const normalizedRoute = targetRoute.startsWith('/') ? targetRoute : `/${targetRoute}`;
  
  // Build base URL
  let url = `${config.appWebUrl}${normalizedRoute}`;
  
  // Append query parameters if provided
  if (queryParams && Object.keys(queryParams).length > 0) {
    const searchParams = new URLSearchParams(queryParams);
    url += `?${searchParams.toString()}`;
  }
  
  return url;
}

/**
 * Redirects to the app-web application
 * @param route - Optional route path (defaults to /restaurants)
 * @param queryParams - Optional query parameters to preserve
 */
export function redirectToApp(
  route?: string,
  queryParams?: Record<string, string>
): void {
  const url = buildAppWebUrl(route, queryParams);
  window.location.href = url;
}

/**
 * Redirects to the restaurants page in app-web
 * This is the primary CTA action for "Order Now" and "Browse Restaurants"
 */
export function redirectToRestaurants(queryParams?: Record<string, string>): void {
  redirectToApp('/restaurants', queryParams);
}
